export const TUTORIAL_VERSION = '1.4.0';

export const TUTORIALS = {
  'student-dashboard': {
    id: 'student-dashboard',
    role: 'student',
    title: 'Welcome to Your Student Dashboard',
    steps: [
      {
        id: 'welcome',
        target: null,
        title: 'Welcome to Austuition!',
        description: 'This short tour walks you through each dashboard tab. You will see where to book tutors, join lessons, check progress, message tutors, submit homework, and handle billing. Take about two minutes; you can restart it anytime from Help.',
        position: 'center',
        showSkip: true,
      },
      {
        id: 'book-trial-tab',
        target: '[data-tutorial="book-tab"]',
        title: 'Book Your Free Trial',
        description: 'Start here when you want a new tutor. Search by subject and year level, open a profile, then request a free trial lesson. After a trial, you can book paid lessons with the same tutor from this tab.',
        position: 'bottom',
        highlight: 'pulse',
      },
      {
        id: 'current-lessons-tab',
        target: '[data-tutorial="lessons-tab"]',
        title: 'Your Current Lessons',
        description: 'This is your day-of hub. Upcoming and active lessons list the tutor, subject, time, and status. Open this tab before a lesson so you can join on time and see any last-minute notes.',
        position: 'bottom',
      },
      {
        id: 'join-meeting-button',
        target: '[data-tutorial="join-meeting-demo"]',
        title: 'Join Meeting Button',
        description: 'When a lesson is about to start, Join meeting unlocks here. It opens the Google Meet room for that booking. Arrive a couple of minutes early so audio and video are ready before teaching begins.',
        position: 'left',
      },
      {
        id: 'past-lessons-tab',
        target: '[data-tutorial="past_lessons-tab"]',
        title: 'Past Lessons',
        description: 'After a lesson finishes, it moves here. Open a past lesson to revisit recordings, transcripts, and summaries when they are available. Useful before exams or when catching up on something you missed.',
        position: 'bottom',
      },
      {
        id: 'progress-tab',
        target: '[data-tutorial="progress-tab"]',
        title: 'Progress and Lesson Summary',
        description: 'See how you are tracking over time: lesson ratings, tutor notes, checkpoints, homework follow-through, and suggested next steps. Check in weekly so feedback does not pile up unread.',
        position: 'bottom',
      },
      {
        id: 'chat-tab',
        target: '[data-tutorial="chat-tab"]',
        title: 'Message Your Tutors',
        description: 'Use chat between lessons for prep questions, schedule tweaks, and quick follow-ups. Keep lesson-critical questions here so your tutor sees them next to your booking history.',
        position: 'bottom',
      },
      {
        id: 'submissions-tab',
        target: '[data-tutorial="submissions-tab"]',
        title: 'Submit Your Homework',
        description: 'Active homework and drafting tasks live here. Upload files when work is due, then wait for tutor feedback. Clear this list regularly so nothing overdue sits unnoticed.',
        position: 'bottom',
      },
      {
        id: 'submission-history-tab',
        target: '[data-tutorial="submission_history-tab"]',
        title: 'Submission History',
        description: 'Older homework cycles and past feedback stay archived here. Reopen them when revising for tests or comparing how your writing has improved across terms.',
        position: 'bottom',
      },
      {
        id: 'drafting-tab',
        target: '[data-tutorial="drafting-tab"]',
        title: 'Drafting Support',
        description: 'Paid drafting requests show status here: awaiting approval, in review, or complete. Use this when you have ordered extra written feedback beyond regular homework.',
        position: 'bottom',
      },
      {
        id: 'billing-tab',
        target: '[data-tutorial="billing-tab"]',
        title: 'Billing',
        description: 'See unpaid lessons or submissions and who is responsible for paying (you or a linked parent). Pay outstanding items here so bookings stay in good standing.',
        position: 'bottom',
      },
      {
        id: 'account-links-tab',
        target: '[data-tutorial="account_links-tab"]',
        title: 'Account Links',
        description: 'Link a parent account so they can view progress, chats (as allowed), and help with billing. You can also manage linked connections if access needs to change later.',
        position: 'bottom',
      },
      {
        id: 'complete',
        target: null,
        title: "You're All Set!",
        description: 'You now know where each student tool lives. Bookmark Current Lessons for lesson days, and use Help anytime to replay this tour if you get stuck.',
        position: 'center',
        showRestartButton: false,
      },
    ],
  },

  'parent-dashboard': {
    id: 'parent-dashboard',
    role: 'parent',
    title: 'Welcome to the Parent Portal',
    steps: [
      {
        id: 'welcome',
        target: null,
        title: 'Welcome to the Parent Portal!',
        description: 'This tour maps every parent tab: finding tutors, monitoring lessons and progress, reading chats, following homework, and managing family billing. It takes a couple of minutes, and you can restart it from Help whenever you need a refresher.',
        position: 'center',
        showSkip: true,
      },
      {
        id: 'discover-tab',
        target: '[data-tutorial="discover-tab"]',
        title: 'Discover Tutors',
        description: 'Search and compare tutors by subject, year level, and style before you book. Open profiles to check outcomes and availability, then request a free trial for your student when you find a good fit.',
        position: 'bottom',
      },
      {
        id: 'your-chats-tab',
        target: '[data-tutorial="your_chats-tab"]',
        title: 'Your Chats',
        description: 'These are your direct conversations with tutors. Use them for planning, concerns, scheduling, and anything you want handled adult-to-tutor without going through the student thread.',
        position: 'bottom',
      },
      {
        id: 'student-chats-tab',
        target: '[data-tutorial="student_chats-tab"]',
        title: 'Student Chats',
        description: 'Read student–tutor messages in view-only mode so you stay informed without interrupting the lesson conversation. Handy when you want context on prep, homework, or how sessions are going.',
        position: 'bottom',
      },
      {
        id: 'lessons-tab',
        target: '[data-tutorial="lessons-tab"]',
        title: 'Monitor Student Lessons',
        description: 'See active and upcoming lessons for linked students: tutor, subject, time, and status. Check this tab on lesson days to confirm bookings and know when class is about to start.',
        position: 'bottom',
      },
      {
        id: 'past-lessons-tab',
        target: '[data-tutorial="past_lessons-tab"]',
        title: 'Past Lessons',
        description: 'Completed lessons archive here with recordings, transcripts, and summaries when available. Use it after a busy week or before parent–teacher style check-ins with your tutor.',
        position: 'bottom',
      },
      {
        id: 'progress-tab',
        target: '[data-tutorial="progress-tab"]',
        title: 'Progress and Lesson Summary',
        description: 'Track ratings, tutor notes, checkpoints, homework follow-through, and next-step guidance over time. This is the clearest view of whether tutoring is moving the needle for your student.',
        position: 'bottom',
      },
      {
        id: 'submissions-tab',
        target: '[data-tutorial="submissions-tab"]',
        title: 'Student Submissions',
        description: 'Monitor current homework cycles: what was assigned, what still needs uploading, and what is waiting on tutor review. Nudge your student here when something is overdue.',
        position: 'bottom',
      },
      {
        id: 'submission-history-tab',
        target: '[data-tutorial="submission_history-tab"]',
        title: 'Submission History',
        description: 'Older submission cycles and feedback stay here for long-term patterns. Revisit them when preparing for exams or reviewing how consistently work is being completed.',
        position: 'bottom',
      },
      {
        id: 'drafting-tab',
        target: '[data-tutorial="drafting-tab"]',
        title: 'Drafting',
        description: 'Follow paid drafting requests in view-only mode: pending, in review, or finished. Useful when your student has ordered deeper written feedback beyond standard homework.',
        position: 'bottom',
      },
      {
        id: 'billing-tab',
        target: '[data-tutorial="billing-tab"]',
        title: 'Family Billing Management',
        description: 'See outstanding lesson and submission charges and pay from one place when you are the billing contact. Keeping this clear avoids booking interruptions for your student.',
        position: 'bottom',
      },
      {
        id: 'account-links-tab',
        target: '[data-tutorial="account_links-tab"]',
        title: 'Link Student Accounts',
        description: 'Securely pair parent and student accounts so progress, chats, and billing permissions stay aligned. Add or update links here if siblings join or access needs to change.',
        position: 'bottom',
      },
      {
        id: 'complete',
        target: null,
        title: "You're All Set!",
        description: 'You now have a full map of the parent portal. On busy weeks, start with Lessons and Progress; use Help anytime to replay this tour.',
        position: 'center',
        showRestartButton: false,
      },
    ],
  },

  'tutor-dashboard': {
    id: 'tutor-dashboard',
    role: 'tutor',
    title: 'Your Tutor Dashboard map',
    steps: [
      {
        id: 'welcome',
        target: null,
        title: 'Where to click next',
        description: 'The Tutor Academy already covered tiers, pay, and trials. This short tour shows where each job lives in the portal.',
        position: 'center',
        showSkip: true,
      },
      {
        id: 'overview-tab',
        target: '[data-tutorial="overview-tab"]',
        title: 'Overview',
        description: 'Open Overview for your Go live checklist and a snapshot of requests, lessons, and earnings.',
        position: 'bottom',
      },
      {
        id: 'requests-tab',
        target: '[data-tutorial="requests-tab"]',
        title: 'Requests',
        description: 'New trial and paid enquiries land here. Accept, propose a time, or decline.',
        position: 'bottom',
      },
      {
        id: 'bookings-tab',
        target: '[data-tutorial="bookings-tab"]',
        title: 'Current Lessons',
        description: 'Scheduled lessons live here. Use Join meeting within 5 minutes of start.',
        position: 'bottom',
      },
      {
        id: 'join-meeting-button',
        target: null,
        title: 'Join meeting',
        description: 'In Current Lessons, Join meeting creates the Google Meet room. Students unlock shortly after you join.',
        position: 'center',
      },
      {
        id: 'chats-tab',
        target: '[data-tutorial="chats-tab"]',
        title: 'Chats',
        description: 'Message students and parents on-platform. Parents may be on the same threads.',
        position: 'bottom',
      },
      {
        id: 'submissions-tab',
        target: '[data-tutorial="submissions-tab"]',
        title: 'Submissions',
        description: 'Review homework uploads here. Paid drafting has its own Drafting tab.',
        position: 'bottom',
      },
      {
        id: 'earnings-tab',
        target: '[data-tutorial="earnings_analytics-tab"]',
        title: 'Earnings',
        description: 'Track completed lessons, pending payments, and payout history.',
        position: 'bottom',
      },
      {
        id: 'calender-tab',
        target: '[data-tutorial="calender-tab"]',
        title: 'Calendar',
        description: 'Month view of scheduled lessons.',
        position: 'bottom',
      },
      {
        id: 'availability-tab',
        target: '[data-tutorial="availability_editing-tab"]',
        title: 'Availability Editing',
        description: 'Save the weekly windows students can book. Required before publish.',
        position: 'bottom',
      },
      {
        id: 'settings-link',
        target: null,
        title: 'Settings',
        description: 'Upload your photo and connect Stripe here. Edit Profile is separate for bio, subjects, and specialties.',
        position: 'center',
      },
      {
        id: 'go-live',
        target: null,
        title: 'Publish',
        description: 'When every Go live item is green on Overview, publish to appear in Find a Tutor.',
        position: 'center',
      },
      {
        id: 'complete',
        target: null,
        title: 'You\'re All Set!',
        description: 'Finish setting your calendar if you have not already, complete the checklist, then publish your profile to start receiving requests.',
        position: 'center',
        showRestartButton: false,
      },
    ],
  },
};

export function getTutorialById(tutorialId) {
  return TUTORIALS[tutorialId] || null;
}

export function getTutorialForRole(role) {
  const roleMap = {
    student: 'student-dashboard',
    parent: 'parent-dashboard',
    tutor: 'tutor-dashboard',
  };
  return roleMap[role] || 'student-dashboard';
}
